import { isDrayNow } from '../../../lib/config/multitenancy'

export class KindeTokenAdapter {
  static decodeJWT(token) {
    try {
      if (typeof token !== 'string') {
        return token // Already decoded
      }

      // Split JWT and decode payload (middle part)
      const parts = token.split('.')
      if (parts.length !== 3) {
        throw new Error('Invalid JWT format')
      }

      // Decode base64url
      const payload = parts[1]
      const decoded = atob(payload.replace(/-/g, '+').replace(/_/g, '/'))
      return JSON.parse(decoded)
    } catch (error) {
      console.error('Failed to decode JWT token:', error)
      return null
    }
  }

  static mapKindeTokenToLegacyFormat(kindeToken) {
    let invalidReason = null

    // Decode JWT if it's a string
    const decodedToken = this.decodeJWT(kindeToken)
    if (!decodedToken) {
      invalidReason = 'Invalid Kinde token: failed to decode JWT'
    }

    if (!invalidReason && !decodedToken.user_properties) {
      invalidReason = 'Invalid Kinde token: missing user_properties field'
    }

    let uid, companyUid, role
    if (!invalidReason) {
      const { user_properties, roles = [] } = decodedToken
      // Use tenant-specific UID fields based on host
      if (isDrayNow) {
        // DrayNow: prefer dn_user_uid and dn_company_uid, fallback to original
        uid = user_properties.dn_user_uid?.v || user_properties.uid?.v
        companyUid =
          user_properties.dn_company_uid?.v || user_properties.company_uid?.v
      } else {
        // ModalView: prefer mv_user_uid and mv_company_uid, fallback to original
        uid = user_properties.mv_user_uid?.v || user_properties.uid?.v
        companyUid =
          user_properties.mv_company_uid?.v || user_properties.company_uid?.v
      }

      // Provisioning gaps are reported before role gaps: a token minted for
      // the other product carries neither a uid nor roles for this one, and
      // "missing roles" sent real investigations down the wrong path — the
      // account has roles, it just isn't provisioned for this product.
      if (!uid) {
        invalidReason = `This account isn't provisioned for ${
          isDrayNow ? 'DrayNow' : 'ModalView'
        }.`
      } else if (!companyUid) {
        invalidReason =
          'Invalid Kinde token: missing company_uid in user_properties'
      } else if (roles.length === 0) {
        invalidReason = 'Invalid Kinde token: missing roles'
      } else {
        const excludedRoles = ['driver', 'guest-driver', 'guest-carrier']
        const validRole = roles.find(
          (r) => r?.key && !excludedRoles.includes(r.key),
        )
        if (!validRole) {
          const isCarrier = roles.find((r) => r?.key === 'guest-carrier')
          invalidReason = `Open the ${
            isDrayNow ? 'DrayNow' : 'ModalView'
          } mobile app to login with your ${
            isCarrier ? 'carrier' : 'driver'
          } account.`
        } else {
          role = validRole.key
        }
      }
    }

    return {
      uid,
      cid: companyUid,
      rol: role,
      invalidReason,
    }
  }

  /**
   * True when the token carries the other product's identity but none for
   * this one — the signature of Kinde silently reusing a cross-product SSO
   * session (the org hint was honored, the user isn't provisioned here).
   */
  static isCrossTenantToken(kindeToken) {
    const decodedToken = this.decodeJWT(kindeToken)
    const props = decodedToken?.user_properties
    if (!props) return false

    const sharedUid = props.uid?.v
    const mvUid = props.mv_user_uid?.v || sharedUid
    const dnUid = props.dn_user_uid?.v || sharedUid
    return isDrayNow ? !dnUid && !!mvUid : !mvUid && !!dnUid
  }

  static validateKindeToken(kindeToken) {
    const result = this.mapKindeTokenToLegacyFormat(kindeToken)
    if (result.invalidReason) {
      console.error('Kinde token validation failed:', result.invalidReason)
      return false
    }
    return true
  }

  static extractTokenData(kindeToken) {
    const result = this.mapKindeTokenToLegacyFormat(kindeToken)
    if (result.invalidReason) {
      console.error(
        'Failed to extract token data from Kinde token:',
        result.invalidReason,
      )
      return null
    }
    // Remove invalidReason from returned data since it's null (valid token)
    const { invalidReason, ...tokenData } = result
    return tokenData
  }

  static extractPermissions(kindeToken) {
    // Decode JWT if it's a string
    const decodedToken = this.decodeJWT(kindeToken)
    if (!decodedToken) {
      console.warn('Invalid Kinde token: failed to decode for permissions')
      return { mkt: [] }
    }

    // Extract permissions from the 'permissions' array in Kinde token
    const kindePermissions = decodedToken.permissions || []

    // For now, return in the expected legacy format until Kinde permissions are configured
    // When Kinde permissions are properly configured, they should map to marketplace permissions
    return {
      mkt: kindePermissions, // This will be empty array until Kinde permissions are set up
    }
  }

  static extractSpecialtyFreight(kindeToken) {
    const decodedToken = this.decodeJWT(kindeToken)
    if (!decodedToken?.user_properties) {
      return null
    }
    return decodedToken.user_properties.specialty_freight_type?.v || null
  }
}
